import * as moment from "moment";

export function getTodayTimeIntervals(from, to, timeGap, bufTime) {
  const startTime = convertToMoment(from).add(bufTime, "minutes");
  const endTime = convertToMoment(to);

  let time = convertToMoment(from)
    .add(bufTime, "minutes")
    .add(timeGap, "minutes");

  let intervals = [];
  if (startTime.isAfter(moment())) {
    intervals.push(startTime.format("hh:mm a"));
  }
  while (time.isBefore(endTime)) {
    // hiding this for now, revert this if there is any issue with slots
    // if (intervals.length === 0) {
    //   time = time.add(bufTime, "minutes");
    // }
    if (
      time.isAfter(moment().add(bufTime, "minutes")) &&
      time.isBetween(startTime, endTime)
    ) {
      intervals.push(time.format("hh:mm a"));
    }
    time = time.add(timeGap, "minutes");
  }
  if (endTime.isAfter(moment())) {
    intervals.push(endTime.format("hh:mm a"));
  }
  return intervals;
}

export function getTimeIntervals(from, to, timeGap, bufTime) {
  const startTime = convertToMoment(from).add(bufTime, "minutes");
  const endTime = convertToMoment(to);
  // console.log("start time", startTime)
  // console.log("end time", endTime)
  let time = convertToMoment(from).add(timeGap, "minutes");
  let intervals = [startTime.format("hh:mm a")];
  while (time.isBefore(endTime)) {
    if (time.isBetween(startTime, endTime)) {
      intervals.push(time.format("hh:mm a"));
    }
    time = time.add(timeGap, "minutes");
  }
  intervals.push(endTime.format("hh:mm a"));
  return intervals;
}

// For a single day's running_hours, derive the absolute (date-aware) start &
// end of each slot in local time. Handles two flavors of midnight crossing:
//   1. Within-slot: a single slot whose local end is <= its local start
//      (e.g. open 22:00 → 02:00 next day) — bump the end by one day.
//   2. Cross-slot: two slots that are *contiguous in local time* but where
//      one collapsed onto today via convertToMoment's anchoring (e.g. evening
//      slot 17:30-23:59 + early slot 00:00-01:45 represent one continuous
//      shift). Detected by start ≈ end (within a small gap tolerance) AND the
//      "morning" slot starting earlier in clock time than the evening slot.
// Order-agnostic: input array order does not affect the result.
const CONTIGUITY_TOLERANCE_MIN = 5;

export function deriveAbsoluteSlotTimes(running_hours) {
  if (!Array.isArray(running_hours) || running_hours.length === 0) return [];

  const slots = running_hours
    .map((rh, originalIndex) => {
      const localStart = convertToMoment(rh.start);
      let localEnd = convertToMoment(rh.stop);
      if (!localStart.isValid() || !localEnd.isValid()) return null;
      let withinSlotWrap = false;
      if (localEnd.isSameOrBefore(localStart)) {
        localEnd = localEnd.clone().add(1, "day");
        withinSlotWrap = true;
      }
      return {
        originalIndex,
        raw: rh,
        localStart: localStart.clone(),
        localEnd: localEnd.clone(),
        withinSlotWrap,
        crossSlotShifted: false,
      };
    })
    .filter(Boolean);

  // Iteratively detect cross-slot midnight continuations and shift the
  // "morning" half forward by a day. Contiguity is measured in *clock-time*
  // wrap distance through midnight, not absolute moment diff (since both
  // ends start out anchored to today, an absolute diff between 23:59 and
  // 00:00 would read as 1439 minutes rather than 1).
  const clockMin = (m) => m.hour() * 60 + m.minute();
  let safety = slots.length * slots.length + 1;
  let changed = true;
  while (changed && safety-- > 0) {
    changed = false;
    for (let i = 0; i < slots.length && !changed; i++) {
      for (let j = 0; j < slots.length && !changed; j++) {
        if (i === j) continue;
        const a = slots[i];
        const b = slots[j];
        const aEndClock = clockMin(a.localEnd);
        const bStartClock = clockMin(b.localStart);
        const aStartClock = clockMin(a.localStart);
        const wrapMin = (bStartClock - aEndClock + 24 * 60) % (24 * 60);
        if (
          !b.crossSlotShifted &&
          wrapMin <= CONTIGUITY_TOLERANCE_MIN &&
          bStartClock < aStartClock &&
          b.localStart.isSame(a.localStart, "day")
        ) {
          b.localStart = b.localStart.clone().add(1, "day");
          b.localEnd = b.localEnd.clone().add(1, "day");
          b.crossSlotShifted = true;
          changed = true;
        }
      }
    }
  }

  return slots;
}

// True when the day's running_hours pattern intrinsically wraps local
// midnight — either a single slot crossing midnight (within-slot wrap) or
// two slots that are clock-contiguous through midnight (cross-slot). This
// is purely a property of the schedule and does not depend on holidays
// or whether today/tomorrow is operational.
export function isRunningHoursMidnightPattern(running_hours) {
  const slots = deriveAbsoluteSlotTimes(running_hours);
  return slots.some((s) => s.crossSlotShifted || s.withinSlotWrap);
}

// Returns the running_hours entry that represents the day's "morning
// overnight extension" (the slot whose clock-time starts contiguously
// after another slot's end through midnight). For a 06:00→23:59 + 00:00→
// 03:00 pattern this returns the 00:00→03:00 entry. Returns null if the
// pattern isn't a midnight wrap. Use this to filter slots when the
// previous day was closed or when the current day is a holiday-but-
// extension day.
export function findOvernightExtensionSlot(running_hours) {
  const slots = deriveAbsoluteSlotTimes(running_hours);
  const shifted = slots.find((s) => s.crossSlotShifted);
  return shifted ? shifted.raw : null;
}

export function convertToMoment(time) {
  const dayStart = moment().startOf("D").format("YYYY-MM-DD");
  if (!time) return moment.invalid();

  // Backend encodes "next-day UTC" hours as 24+ (e.g. "24:30:00" = 00:30 UTC next day).
  // ISO 8601 / moment reject hour > 23, so normalize before parsing.
  const [hStr, mStr = "00", sStr = "00"] = String(time).split(":");
  let hour = parseInt(hStr, 10);
  let baseDate = dayStart;
  if (!isNaN(hour) && hour >= 24) {
    const extraDays = Math.floor(hour / 24);
    hour = hour % 24;
    baseDate = moment(dayStart).add(extraDays, "day").format("YYYY-MM-DD");
  }
  const normalizedTime = `${String(hour).padStart(2, "0")}:${mStr}:${sStr}`;

  const localDate = moment.utc(baseDate + " " + normalizedTime).local();
  const newTime = localDate.format("HH:mm");

  return moment(dayStart + " " + newTime);
}
